﻿import { FunctionComponent, useState } from "react";
import { Card, CardBody, CardHeader, Heading, Icon, IconButton, Td, Tr } from "@chakra-ui/react";
import { IconTrash } from "@tabler/icons-react";
import { TableResult, useConfirm } from "@am-tax/fe-core";
import { useCandidateSignatures } from "../../api/hooks/useCandidateSignatures";
import { useCandidateSignatureDelete } from "../../api/hooks/useCandidateSignatureDelete";
import { CandidateSignature } from "../../api/models/CandidateSignature";

interface CandidateSignaturesCardProps {
    tenantId: string;
    entityId: string;
    candidateId: string;
}

export const CandidateSignaturesCard: FunctionComponent<CandidateSignaturesCardProps> = ({ tenantId, entityId, candidateId }) => {
    const signaturesQuery = useCandidateSignatures(tenantId, entityId, candidateId);
    const deleteSignature = useCandidateSignatureDelete();
    const [signatureToDelete, setSignatureToDelete] = useState<CandidateSignature | null>(null);
    const { ConfirmDialog, confirm } = useConfirm({ prompt: "Are you sure you want to delete this signature?" });

    const handleDelete = async (signature: CandidateSignature) => {
        const confirmed = await confirm();

        if (confirmed) {
            setSignatureToDelete(signature);
            deleteSignature.mutate(
                { id: signature.id, tenantId, entityId, candidateId },
                {
                    onSuccess: async () => {
                        await signaturesQuery.refetch();
                    },
                }
            );
        }
    };

    return (
        <Card variant={"withShadow"} mb={"1.5rem"}>
            <ConfirmDialog />
            <CardHeader pb={0}>
                <Heading size={"sm"}>Signatures</Heading>
            </CardHeader>
            <CardBody>
                <TableResult query={signaturesQuery} columns={["Type", "Description", "Signed On", "Actions"]} size={"sm"}>
                    {signaturesQuery.data?.map(signature => (
                        <Tr key={signature.id}>
                            <Td>{signature.signatureType}</Td>
                            <Td>{signature.description}</Td>
                            <Td>{signature.createdOn}</Td>
                            <Td>
                                <IconButton
                                    icon={<Icon as={IconTrash} />}
                                    aria-label={"Delete signature"}
                                    size={"sm"}
                                    variant={"ghost"}
                                    isLoading={deleteSignature.isLoading && signatureToDelete?.id === signature.id}
                                    isDisabled={deleteSignature.isLoading}
                                    onClick={() => handleDelete(signature)}
                                />
                            </Td>
                        </Tr>
                    ))}
                </TableResult>
            </CardBody>
        </Card>
    );
};
